import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ColumnConfig } from '@/components/ColumnSettingsPopover';

interface ColumnsState {
	assetsRegister: ColumnConfig[];
	assetsRegisterHierarchy: ColumnConfig[];
	serviceContracts: ColumnConfig[];
	servicePartners: ColumnConfig[];
	rules: ColumnConfig[];
	procedures: ColumnConfig[];
	workOrders: ColumnConfig[];
	departments: ColumnConfig[];
	areas: ColumnConfig[];
	sites: ColumnConfig[];
	usersRegister: ColumnConfig[];
	resources: ColumnConfig[];
	maintenanceTeams: ColumnConfig[];
	disciplines: ColumnConfig[];
}

const defaultColumns: ColumnConfig[] = [
	{ id: 'index', label: '#', visible: true },
	{ id: 'id', label: 'ID', visible: false },
	{ id: 'name', label: 'Name', visible: true, disabled: true },
	{ id: 'category', label: 'Category', visible: true },
	{ id: 'type', label: 'Type', visible: true },
	{ id: 'group', label: 'Group', visible: true },
	{ id: 'site', label: 'Site', visible: true },
	{ id: 'area', label: 'Area', visible: true },
	{ id: 'status', label: 'Status', visible: true },
	{ id: 'available', label: 'Available', visible: true },
	{ id: 'maintenance', label: 'Maintenance', visible: true },
	{ id: 'lastMaintenanceDate', label: 'Last Maintenance Date', visible: true },
	{ id: 'nextMaintenanceDate', label: 'Next Maintenance Date', visible: true },
];

const defaultServiceContractsColumns: ColumnConfig[] = [
	{ id: 'index', label: '#', visible: true },
	{ id: 'id', label: 'ID', visible: false },
	{ id: 'name', label: 'Contract Name', visible: true, disabled: true },
	{ id: 'servicePartner', label: 'Service Partner', visible: true },
	{ id: 'servicePartnerEmail', label: 'Service Partner Email', visible: true },
	{ id: 'serviceLevel', label: 'Service Level', visible: true },
	{ id: 'contractPeriod', label: 'Contract Period', visible: true },
	{ id: 'assetsCount', label: 'Assets Count', visible: true },
	{ id: 'annualCost', label: 'Annual Cost', visible: true },
	{ id: 'files', label: 'Files', visible: true },
	{ id: 'status', label: 'Status', visible: true },
];

const defaultServicePartnersColumns: ColumnConfig[] = [
	{ id: 'index', label: '#', visible: false },
	{ id: 'id', label: 'ID', visible: false },
	{ id: 'name', label: 'Name', visible: true, disabled: true },
	{ id: 'email', label: 'Email', visible: true },
	{ id: 'status', label: 'Status', visible: true },
];

const defaultRulesColumns: ColumnConfig[] = [
	{ id: 'index', label: '#', visible: true },
	{ id: 'id', label: 'ID', visible: false },
	{ id: 'name', label: 'Name', visible: true, disabled: true },
	{ id: 'type', label: 'Type', visible: true },
	{ id: 'triggerSummary', label: 'Trigger Summary', visible: true },
	{ id: 'createdBy', label: 'Created By', visible: true },
	{ id: 'createdDate', label: 'Created Date', visible: true },
	{ id: 'assetsCount', label: 'Assets Count', visible: true },
	{ id: 'status', label: 'Status', visible: true },
];

const defaultProceduresColumns: ColumnConfig[] = [
	{ id: 'index', label: '#', visible: true },
	{ id: 'id', label: 'ID', visible: false },
	{ id: 'name', label: 'Name', visible: true, disabled: true },
	{ id: 'type', label: 'Type', visible: true },
	{ id: 'category', label: 'Category', visible: true },
	{ id: 'stepsCount', label: 'Steps', visible: true },
	{ id: 'status', label: 'Status', visible: true },
	{ id: 'createdBy', label: 'Created By', visible: false },
	{ id: 'createdDate', label: 'Created Date', visible: false },
];

const defaultWorkOrdersColumns: ColumnConfig[] = [
	{ id: 'index', label: '#', visible: true },
	// A new id (not 'id') so saved column settings, which hid 'id', don't keep it hidden.
	{ id: 'woNumber', label: 'WO No.', visible: true },
	{ id: 'source', label: 'Source', visible: true },
	{ id: 'assetName', label: 'Asset', visible: true },
	{ id: 'dueDate', label: 'Due Date', visible: true },
	{ id: 'status', label: 'Status', visible: true },
	{ id: 'priority', label: 'Priority', visible: true },
	{ id: 'isStatutory', label: 'Statutory', visible: true },
	{ id: 'urgency', label: 'Urgency', visible: true },
	{ id: 'category', label: 'Category', visible: true },
	{ id: 'estimatedDuration', label: 'Estimated Duration', visible: true },
	{ id: 'assignedTo', label: 'Assigned To', visible: true },
	{ id: 'siteName', label: 'Site', visible: true },
	{ id: 'createdAt', label: 'Created Date', visible: true },
];

const defaultDepartmentsColumns: ColumnConfig[] = [
	{ id: 'index', label: '#', visible: false },
	{ id: 'id', label: 'ID', visible: false },
	{ id: 'name', label: 'Name', visible: true, disabled: true },
	{ id: 'site', label: 'Site', visible: true },
	{ id: 'manager', label: 'Manager', visible: true },
	{ id: 'users', label: 'Users', visible: true },
];

const defaultAreasColumns: ColumnConfig[] = [
	{ id: 'index', label: '#', visible: true },
	{ id: 'id', label: 'ID', visible: false },
	{ id: 'name', label: 'Name', visible: true, disabled: true },
	{ id: 'description', label: 'Description', visible: true },
	{ id: 'site', label: 'Site', visible: true },
	{ id: 'assetsCount', label: 'Assets Count', visible: true },
];

const defaultDisciplinesColumns: ColumnConfig[] = [
	{ id: 'index', label: '#', visible: false },
	{ id: 'id', label: 'ID', visible: false },
	{ id: 'name', label: 'Name', visible: true, disabled: true },
	{ id: 'description', label: 'Description', visible: true },
	{ id: 'resourcesCount', label: 'Resources', visible: true },
	{ id: 'status', label: 'Status', visible: true },
];

const defaultResourcesColumns: ColumnConfig[] = [
	{ id: 'index', label: '#', visible: true },
	{ id: 'id', label: 'ID', visible: false },
	{ id: 'name', label: 'Name', visible: true, disabled: true },
	{ id: 'discipline', label: 'Discipline', visible: true },
	{ id: 'area', label: 'Area', visible: true },
	{ id: 'site', label: 'Site', visible: true },
	{ id: 'teams', label: 'Teams', visible: true },
	{ id: 'dailyCapacityHours', label: 'Daily Capacity (h)', visible: true },
	{ id: 'weeklyWorkDays', label: 'Weekly Work Days', visible: true },
	{ id: 'weeklyCapacityHours', label: 'Weekly Capacity (h)', visible: true },
	{ id: 'status', label: 'Status', visible: true },
];

const defaultMaintenanceTeamsColumns: ColumnConfig[] = [
	{ id: 'index', label: '#', visible: false },
	{ id: 'id', label: 'ID', visible: false },
	{ id: 'name', label: 'Name', visible: true, disabled: true },
	{ id: 'description', label: 'Description', visible: true },
	{ id: 'site', label: 'Site', visible: true },
	{ id: 'teamLead', label: 'Team Lead', visible: true },
	{ id: 'members', label: 'Members', visible: true },
	{ id: 'weeklyCapacityHours', label: 'Weekly Capacity (h)', visible: true },
	{ id: 'status', label: 'Status', visible: true },
];

const defaultSitesColumns: ColumnConfig[] = [
	{ id: 'index', label: '#', visible: false },
	{ id: 'id', label: 'ID', visible: false },
	{ id: 'name', label: 'Name', visible: true, disabled: true },
	{ id: 'location', label: 'Location', visible: true },
	{ id: 'assetsCount', label: 'Assets Count', visible: true },
	{ id: 'departmentsCount', label: 'Departments Count', visible: true },
];

// The Organisation column is deliberately absent: it is a GlobalAdmin-only column driven by
// permissions (see useOrganisationColumn), not something a user can toggle here.
const defaultUsersRegisterColumns: ColumnConfig[] = [
	{ id: 'index', label: '#', visible: true },
	{ id: 'id', label: 'ID', visible: false },
	{ id: 'name', label: 'Name', visible: true, disabled: true },
	{ id: 'email', label: 'Email', visible: true },
	{ id: 'role', label: 'Role', visible: true },
	{ id: 'department', label: 'Department', visible: true },
	{ id: 'site', label: 'Site', visible: true },
	{ id: 'pinCode', label: 'Pin Code', visible: true },
	{ id: 'status', label: 'Status', visible: true },
	{ id: 'createdAt', label: 'Created Date', visible: true },
];

// Keeps the user's saved order/visibility while pulling in columns added after they were saved.
const mergeWithDefaults = (stored: ColumnConfig[], defaults: ColumnConfig[]): ColumnConfig[] => {
	const storedById = new Map(stored.map((col) => [col.id, col]));
	const knownIds = new Set(defaults.map((col) => col.id));
	const merged = stored.filter((col) => knownIds.has(col.id));

	defaults.forEach((col, index) => {
		if (!storedById.has(col.id)) merged.splice(index, 0, col);
	});

	return merged;
};

const loadColumnsFromStorage = (): ColumnConfig[] => {
	try {
		const stored = localStorage.getItem('assetsRegisterColumns');
		return stored ? mergeWithDefaults(JSON.parse(stored), defaultColumns) : defaultColumns;
	} catch {
		return defaultColumns;
	}
};

const loadAssetsRegisterHierarchyColumnsFromStorage = (): ColumnConfig[] => {
	try {
		const stored = localStorage.getItem('assetsRegisterHierarchyColumns');
		return stored ? mergeWithDefaults(JSON.parse(stored), defaultColumns) : defaultColumns;
	} catch {
		return defaultColumns;
	}
};

const loadServiceContractsColumnsFromStorage = (): ColumnConfig[] => {
	try {
		const stored = localStorage.getItem('serviceContractsColumns');
		return stored
			? mergeWithDefaults(JSON.parse(stored), defaultServiceContractsColumns)
			: defaultServiceContractsColumns;
	} catch {
		return defaultServiceContractsColumns;
	}
};

const loadServicePartnersColumnsFromStorage = (): ColumnConfig[] => {
	try {
		const stored = localStorage.getItem('servicePartnersColumns');
		return stored
			? mergeWithDefaults(JSON.parse(stored), defaultServicePartnersColumns)
			: defaultServicePartnersColumns;
	} catch {
		return defaultServicePartnersColumns;
	}
};

const loadRulesColumnsFromStorage = (): ColumnConfig[] => {
	try {
		const stored = localStorage.getItem('rulesColumns');
		return stored ? mergeWithDefaults(JSON.parse(stored), defaultRulesColumns) : defaultRulesColumns;
	} catch {
		return defaultRulesColumns;
	}
};

const loadProceduresColumnsFromStorage = (): ColumnConfig[] => {
	try {
		const stored = localStorage.getItem('proceduresColumns');
		return stored ? mergeWithDefaults(JSON.parse(stored), defaultProceduresColumns) : defaultProceduresColumns;
	} catch {
		return defaultProceduresColumns;
	}
};

const loadWorkOrdersColumnsFromStorage = (): ColumnConfig[] => {
	try {
		const stored = localStorage.getItem('workOrdersColumns');
		if (!stored) return defaultWorkOrdersColumns;
		const parsed: ColumnConfig[] = JSON.parse(stored);
		// Migrate: drop legacy 'title' column if present
		if (parsed.some((c) => c.id === 'title')) {
			localStorage.removeItem('workOrdersColumns');
			return defaultWorkOrdersColumns;
		}
		// Merged with defaults so a layout stored before a column was added still picks it up.
		return mergeWithDefaults(parsed, defaultWorkOrdersColumns);
	} catch {
		return defaultWorkOrdersColumns;
	}
};

const loadDepartmentsColumnsFromStorage = (): ColumnConfig[] => {
	try {
		const stored = localStorage.getItem('departmentsColumns');
		return stored ? mergeWithDefaults(JSON.parse(stored), defaultDepartmentsColumns) : defaultDepartmentsColumns;
	} catch {
		return defaultDepartmentsColumns;
	}
};

// Merged with defaults so a stored layout from before a column was added still picks it up,
// the same way the users register does.
const loadAreasColumnsFromStorage = (): ColumnConfig[] => {
	try {
		const stored = localStorage.getItem('areasColumns');
		return stored ? mergeWithDefaults(JSON.parse(stored), defaultAreasColumns) : defaultAreasColumns;
	} catch {
		return defaultAreasColumns;
	}
};

const loadResourcesColumnsFromStorage = (): ColumnConfig[] => {
	try {
		const stored = localStorage.getItem('resourcesColumns');
		return stored ? mergeWithDefaults(JSON.parse(stored), defaultResourcesColumns) : defaultResourcesColumns;
	} catch {
		return defaultResourcesColumns;
	}
};

const loadMaintenanceTeamsColumnsFromStorage = (): ColumnConfig[] => {
	try {
		const stored = localStorage.getItem('maintenanceTeamsColumns');
		return stored
			? mergeWithDefaults(JSON.parse(stored), defaultMaintenanceTeamsColumns)
			: defaultMaintenanceTeamsColumns;
	} catch {
		return defaultMaintenanceTeamsColumns;
	}
};

const loadSitesColumnsFromStorage = (): ColumnConfig[] => {
	try {
		const stored = localStorage.getItem('sitesColumns');
		return stored ? mergeWithDefaults(JSON.parse(stored), defaultSitesColumns) : defaultSitesColumns;
	} catch {
		return defaultSitesColumns;
	}
};

const loadDisciplinesColumnsFromStorage = (): ColumnConfig[] => {
	try {
		const stored = localStorage.getItem('disciplinesColumns');
		return stored ? mergeWithDefaults(JSON.parse(stored), defaultDisciplinesColumns) : defaultDisciplinesColumns;
	} catch {
		return defaultDisciplinesColumns;
	}
};

const loadUsersRegisterColumnsFromStorage = (): ColumnConfig[] => {
	try {
		const stored = localStorage.getItem('usersRegisterColumns');
		return stored ? mergeWithDefaults(JSON.parse(stored), defaultUsersRegisterColumns) : defaultUsersRegisterColumns;
	} catch {
		return defaultUsersRegisterColumns;
	}
};

const COLUMN_STORAGE_KEYS = [
	'assetsRegisterColumns',
	'assetsRegisterHierarchyColumns',
	'serviceContractsColumns',
	'servicePartnersColumns',
	'rulesColumns',
	'proceduresColumns',
	'workOrdersColumns',
	'departmentsColumns',
	'areasColumns',
	'sitesColumns',
	'usersRegisterColumns',
	'resourcesColumns',
	'maintenanceTeamsColumns',
	'disciplinesColumns',
];

// One-off: the database ID column was replaced by the '#' row counter and is hidden everywhere.
// Layouts saved before that decision still carry `id` as a visible, locked column, and may carry
// `index` at an earlier default. Dropping `index` lets mergeWithDefaults re-seed it from the
// current defaults, so a saved layout does not pin either column to a superseded decision.
const COLUMN_MIGRATION_KEY = 'columnsIdIndexMigrationV2';

const runColumnMigration = () => {
	try {
		if (localStorage.getItem(COLUMN_MIGRATION_KEY)) return;

		COLUMN_STORAGE_KEYS.forEach((key) => {
			const stored = localStorage.getItem(key);
			if (!stored) return;

			const parsed: ColumnConfig[] = JSON.parse(stored);
			const migrated = parsed
				.filter((col) => col.id !== 'index')
				.map((col) => (col.id === 'id' ? { ...col, visible: false, disabled: false } : col));
			localStorage.setItem(key, JSON.stringify(migrated));
		});

		localStorage.setItem(COLUMN_MIGRATION_KEY, '1');
	} catch {
		// Unreadable storage just means the defaults apply, which are already correct.
	}
};

runColumnMigration();

// One-off: Status moved ahead of Priority in the work orders table. A layout saved before that
// keeps its own order, so reposition the stored entry instead of resetting the whole layout.
const WORK_ORDERS_STATUS_ORDER_KEY = 'workOrdersStatusBeforePriorityV1';

const runWorkOrdersStatusOrderMigration = () => {
	try {
		if (localStorage.getItem(WORK_ORDERS_STATUS_ORDER_KEY)) return;

		const stored = localStorage.getItem('workOrdersColumns');
		if (stored) {
			const parsed: ColumnConfig[] = JSON.parse(stored);
			const priorityIndex = parsed.findIndex((col) => col.id === 'priority');
			const statusIndex = parsed.findIndex((col) => col.id === 'status');

			if (priorityIndex !== -1 && statusIndex > priorityIndex) {
				const [statusColumn] = parsed.splice(statusIndex, 1);
				parsed.splice(priorityIndex, 0, statusColumn);
				localStorage.setItem('workOrdersColumns', JSON.stringify(parsed));
			}
		}

		localStorage.setItem(WORK_ORDERS_STATUS_ORDER_KEY, '1');
	} catch {
		// Unreadable storage just means the defaults apply, which are already correct.
	}
};

runWorkOrdersStatusOrderMigration();

const initialState: ColumnsState = {
	assetsRegister: loadColumnsFromStorage(),
	assetsRegisterHierarchy: loadAssetsRegisterHierarchyColumnsFromStorage(),
	serviceContracts: loadServiceContractsColumnsFromStorage(),
	servicePartners: loadServicePartnersColumnsFromStorage(),
	rules: loadRulesColumnsFromStorage(),
	procedures: loadProceduresColumnsFromStorage(),
	workOrders: loadWorkOrdersColumnsFromStorage(),
	departments: loadDepartmentsColumnsFromStorage(),
	areas: loadAreasColumnsFromStorage(),
	sites: loadSitesColumnsFromStorage(),
	usersRegister: loadUsersRegisterColumnsFromStorage(),
	resources: loadResourcesColumnsFromStorage(),
	maintenanceTeams: loadMaintenanceTeamsColumnsFromStorage(),
	disciplines: loadDisciplinesColumnsFromStorage(),
};

const columnsSlice = createSlice({
	name: 'columns',
	initialState,
	reducers: {
		setAssetsRegisterColumns: (state, action: PayloadAction<ColumnConfig[]>) => {
			state.assetsRegister = action.payload;
			localStorage.setItem('assetsRegisterColumns', JSON.stringify(action.payload));
		},
		resetAssetsRegisterColumns: (state) => {
			state.assetsRegister = defaultColumns;
			localStorage.removeItem('assetsRegisterColumns');
		},
		setAssetsRegisterHierarchyColumns: (state, action: PayloadAction<ColumnConfig[]>) => {
			state.assetsRegisterHierarchy = action.payload;
			localStorage.setItem('assetsRegisterHierarchyColumns', JSON.stringify(action.payload));
		},
		resetAssetsRegisterHierarchyColumns: (state) => {
			state.assetsRegisterHierarchy = defaultColumns;
			localStorage.removeItem('assetsRegisterHierarchyColumns');
		},
		setServiceContractsColumns: (state, action: PayloadAction<ColumnConfig[]>) => {
			state.serviceContracts = action.payload;
			localStorage.setItem('serviceContractsColumns', JSON.stringify(action.payload));
		},
		resetServiceContractsColumns: (state) => {
			state.serviceContracts = defaultServiceContractsColumns;
			localStorage.removeItem('serviceContractsColumns');
		},
		setServicePartnersColumns: (state, action: PayloadAction<ColumnConfig[]>) => {
			state.servicePartners = action.payload;
			localStorage.setItem('servicePartnersColumns', JSON.stringify(action.payload));
		},
		resetServicePartnersColumns: (state) => {
			state.servicePartners = defaultServicePartnersColumns;
			localStorage.removeItem('servicePartnersColumns');
		},
		setRulesColumns: (state, action: PayloadAction<ColumnConfig[]>) => {
			state.rules = action.payload;
			localStorage.setItem('rulesColumns', JSON.stringify(action.payload));
		},
		resetRulesColumns: (state) => {
			state.rules = defaultRulesColumns;
			localStorage.removeItem('rulesColumns');
		},
		setProceduresColumns: (state, action: PayloadAction<ColumnConfig[]>) => {
			state.procedures = action.payload;
			localStorage.setItem('proceduresColumns', JSON.stringify(action.payload));
		},
		resetProceduresColumns: (state) => {
			state.procedures = defaultProceduresColumns;
			localStorage.removeItem('proceduresColumns');
		},
		setWorkOrdersColumns: (state, action: PayloadAction<ColumnConfig[]>) => {
			state.workOrders = action.payload;
			localStorage.setItem('workOrdersColumns', JSON.stringify(action.payload));
		},
		resetWorkOrdersColumns: (state) => {
			state.workOrders = defaultWorkOrdersColumns;
			localStorage.removeItem('workOrdersColumns');
		},
		setDepartmentsColumns: (state, action: PayloadAction<ColumnConfig[]>) => {
			state.departments = action.payload;
			localStorage.setItem('departmentsColumns', JSON.stringify(action.payload));
		},
		resetDepartmentsColumns: (state) => {
			state.departments = defaultDepartmentsColumns;
			localStorage.removeItem('departmentsColumns');
		},
		setAreasColumns: (state, action: PayloadAction<ColumnConfig[]>) => {
			state.areas = action.payload;
			localStorage.setItem('areasColumns', JSON.stringify(action.payload));
		},
		resetAreasColumns: (state) => {
			state.areas = defaultAreasColumns;
			localStorage.removeItem('areasColumns');
		},
		setResourcesColumns: (state, action: PayloadAction<ColumnConfig[]>) => {
			state.resources = action.payload;
			localStorage.setItem('resourcesColumns', JSON.stringify(action.payload));
		},
		resetResourcesColumns: (state) => {
			state.resources = defaultResourcesColumns;
			localStorage.removeItem('resourcesColumns');
		},
		setMaintenanceTeamsColumns: (state, action: PayloadAction<ColumnConfig[]>) => {
			state.maintenanceTeams = action.payload;
			localStorage.setItem('maintenanceTeamsColumns', JSON.stringify(action.payload));
		},
		resetMaintenanceTeamsColumns: (state) => {
			state.maintenanceTeams = defaultMaintenanceTeamsColumns;
			localStorage.removeItem('maintenanceTeamsColumns');
		},
		setDisciplinesColumns: (state, action: PayloadAction<ColumnConfig[]>) => {
			state.disciplines = action.payload;
			localStorage.setItem('disciplinesColumns', JSON.stringify(action.payload));
		},
		resetDisciplinesColumns: (state) => {
			state.disciplines = defaultDisciplinesColumns;
			localStorage.removeItem('disciplinesColumns');
		},
		setSitesColumns: (state, action: PayloadAction<ColumnConfig[]>) => {
			state.sites = action.payload;
			localStorage.setItem('sitesColumns', JSON.stringify(action.payload));
		},
		resetSitesColumns: (state) => {
			state.sites = defaultSitesColumns;
			localStorage.removeItem('sitesColumns');
		},
		setUsersRegisterColumns: (state, action: PayloadAction<ColumnConfig[]>) => {
			state.usersRegister = action.payload;
			localStorage.setItem('usersRegisterColumns', JSON.stringify(action.payload));
		},
		resetUsersRegisterColumns: (state) => {
			state.usersRegister = defaultUsersRegisterColumns;
			localStorage.removeItem('usersRegisterColumns');
		},
	},
});

export const {
	setAssetsRegisterColumns,
	resetAssetsRegisterColumns,
	setAssetsRegisterHierarchyColumns,
	resetAssetsRegisterHierarchyColumns,
	setServiceContractsColumns,
	resetServiceContractsColumns,
	setServicePartnersColumns,
	resetServicePartnersColumns,
	setRulesColumns,
	resetRulesColumns,
	setProceduresColumns,
	resetProceduresColumns,
	setWorkOrdersColumns,
	resetWorkOrdersColumns,
	setDepartmentsColumns,
	resetDepartmentsColumns,
	setSitesColumns,
	resetSitesColumns,
	setUsersRegisterColumns,
	resetUsersRegisterColumns,
	setAreasColumns,
	resetAreasColumns,
	setResourcesColumns,
	resetResourcesColumns,
	setMaintenanceTeamsColumns,
	resetMaintenanceTeamsColumns,
	setDisciplinesColumns,
	resetDisciplinesColumns,
} = columnsSlice.actions;
export {
	defaultColumns,
	defaultServiceContractsColumns,
	defaultServicePartnersColumns,
	defaultRulesColumns,
	defaultProceduresColumns,
	defaultWorkOrdersColumns,
	defaultDepartmentsColumns,
	defaultAreasColumns,
	defaultSitesColumns,
	defaultUsersRegisterColumns,
	defaultResourcesColumns,
	defaultMaintenanceTeamsColumns,
	defaultDisciplinesColumns,
};
export default columnsSlice.reducer;
